import type React from 'react'

import { useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { cn } from '../utils/cn'

interface CustomOTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  className?: string
}

export function CustomOTPInput({
  length = 6,
  value,
  onChange,
  className,
}: CustomOTPInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    // iOS / Android security-code autofill drops the full OTP into the focused
    // input as a single change event. Distribute the digits across the boxes
    // instead of taking only the first one.
    if (newValue.length > 1) {
      const digits = newValue.replace(/\D/g, '').slice(0, length)
      if (!digits) return
      onChange(digits)
      const nextIndex = Math.min(digits.length, length - 1)
      inputRefs.current[nextIndex]?.focus()
      return
    }

    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) return

    // Update the value
    const newOTP = value.split('')
    newOTP[index] = newValue
    onChange(newOTP.join(''))

    // Move to next input if value entered
    if (newValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // If current input is empty, move to previous input
        inputRefs.current[index - 1]?.focus()
      } else {
        // Clear current input
        const newOTP = value.split('')
        newOTP[index] = ''
        onChange(newOTP.join(''))
      }
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length)

    // Only allow numbers
    if (!/^\d+$/.test(pastedData)) return

    onChange(pastedData.padEnd(length, ''))

    // Focus the next empty input or the last input
    const nextIndex = Math.min(pastedData.length, length - 1)
    inputRefs.current[nextIndex]?.focus()
  }

  return (
    <div className={cn('flex gap-1.5 sm:gap-2 w-full max-w-sm', className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={el => {
            inputRefs.current[index] = el
          }}
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          maxLength={length}
          value={value[index] || ''}
          onChange={e => handleChange(index, e)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          className={cn(
            'flex-1 min-w-0 aspect-square max-w-16 text-center text-lg font-medium text-black',
            'border border-gray-300 rounded-md',
            'focus:outline-none focus:ring-2 focus:ring-[#0A2540] focus:border-transparent',
            'transition-all duration-200',
            focusedIndex === index && 'ring-2 ring-[#0A2540] border-transparent'
          )}
        />
      ))}
    </div>
  )
}
